import { IcArrowTriangleFilled } from '@assets/icons/24x24';
import { Box, Button, Center, HStack, Icon, Text, VStack } from '@chakra-ui/react';
import { Checkbox } from '@components/ui/checkbox/Checkbox';
import useClickOutside from '@hooks/useClickOutside';
import { FunctionComponent, memo, ReactElement, ReactNode, SVGProps } from 'react';
import { FormattedMessage } from 'react-intl';

type DropOption<T> = {
  value: T;
  label: string;
};

type WrapperType =
  | { icon: FunctionComponent<SVGProps<SVGSVGElement>>; label?: never }
  | { icon?: never; label: string };

type Props<T> = {
  options: DropOption<T>[];
  values: Set<T>;
  padding?: number | string;
  getOptionIcon?: (value: T) => FunctionComponent<SVGProps<SVGSVGElement>>;
  onChange: (values: Set<T>) => void;
} & WrapperType;

function DropSelect<T>({ label, options, values, padding = '6px', onChange, getOptionIcon, icon }: Readonly<Props<T>>) {
  const { ref, isOpen, toggle } = useClickOutside();

  const allActive = values.size === options.length;
  const allInactive = values.size === 0;

  function getWrapper(children: ReactNode) {
    if (icon) {
      const iconColor = isOpen ? 'neutral.white' : 'neutral.300';
      return (
        <HStack
          ref={ref}
          gap={1}
          paddingLeft={1}
          paddingRight={0.5}
          paddingY={padding}
          border="2px solid"
          borderColor="blue.600"
          backgroundColor={isOpen ? 'skyBlue' : 'transparent'}
          cursor="pointer"
          position="relative"
          onClick={(e) => {
            toggle();
            e.stopPropagation();
          }}
        >
          <Icon as={icon} boxSize="24px" color={iconColor} />
          <Icon
            as={IcArrowTriangleFilled}
            width="24px"
            height="24px"
            transform={isOpen ? 'rotate(180deg)' : undefined}
            color={iconColor}
          />
          {isOpen && children}
        </HStack>
      );
    } else {
      return (
        <HStack
          ref={ref}
          gap={0.25}
          flexShrink={0}
          width="100%"
          border="2px solid"
          borderColor="neutral.black"
          backgroundColor="neutral.black"
          position="relative"
          userSelect="none"
          cursor="pointer"
          role="group"
          onClick={(e) => {
            toggle();
            e.stopPropagation();
          }}
        >
          <HStack
            gap={1}
            paddingLeft={2}
            paddingRight={1.5}
            paddingY={1.5}
            backgroundColor="neutral.900"
            width="100%"
            height="100%"
          >
            <Text textStyle="md">
              <FormattedMessage id={label} values={{ count: values.size === options.length ? true : values.size }} />
            </Text>
            <Text textStyle="md" color="skyBlue">
              {values.size}
            </Text>
          </HStack>
          <Center
            border="2px solid"
            borderColor={isOpen ? 'blue' : 'neutral.800'}
            padding={1}
            backgroundColor={isOpen ? 'blue' : 'neutral.black'}
            _groupHover={{
              borderColor: isOpen ? 'cuber.500' : 'skyBlue',
            }}
          >
            <Icon
              as={IcArrowTriangleFilled}
              width="24px"
              height="24px"
              color={isOpen ? 'neutral.white' : 'neutral.300'}
              transform={isOpen ? 'rotate(180deg)' : undefined}
            />
          </Center>
          {isOpen && children}
        </HStack>
      );
    }
  }

  return getWrapper(
    <Box
      position="absolute"
      border="2px solid"
      width="max-content"
      borderColor="neutral.black"
      padding={2}
      backgroundColor="neutral.850"
      left="-2px"
      cursor="default"
      zIndex={999}
      top="50px"
      onClick={(e) => e.stopPropagation()}
    >
      <VStack gap={2} alignItems="start">
        <HStack gap={1}>
          <Button
            variant="toggle"
            size="toggleS"
            disabled={allActive}
            onClick={() => !allActive && onChange(new Set(options.map((option) => option.value)))}
          >
            <FormattedMessage id="global.all" />
          </Button>
          <Button
            variant="toggle"
            size="toggleS"
            disabled={allInactive}
            onClick={() => {
              !allInactive && onChange(new Set());
            }}
          >
            <FormattedMessage id="global.none" />
          </Button>
        </HStack>
        {options.map((option) => (
          <Checkbox
            checked={values.has(option.value)}
            key={`${option.value}`}
            label={
              <HStack gap={1} marginLeft={1}>
                {getOptionIcon ? <Icon as={getOptionIcon(option.value)} boxSize="24px" /> : null}
                <Text lineClamp={1} wordBreak="break-all">
                  {option.label}
                </Text>
              </HStack>
            }
            onChange={() => {
              const newSet = new Set(values);
              newSet.has(option.value) ? newSet.delete(option.value) : newSet.add(option.value);
              onChange(newSet);
            }}
          />
        ))}
      </VStack>
    </Box>,
  );
}
export default memo(DropSelect) as unknown as <T>(props: Props<T>) => ReactElement;
